/**
 * React 入口 - WordPress 集成
 * 注意：不要使用 export，否则会被 tree-shake
 */

import { createRoot } from 'react-dom/client';
import { App } from './App';
import {
  attachSystemThemeListener,
  hydrateThemeAtChatOpen,
  useThemeStore,
} from './stores';

// 导入样式（复用现有 CSS + 浅色主题覆盖）
import './styles/chat.css';
import './styles/chat-theme-light.css';

// 当前 React 实例引用
let currentRoot: ReturnType<typeof createRoot> | null = null;
let currentContainer: HTMLDivElement | null = null;

// WordPress 全局入口函数
function openAiChat(serverId: number, serverName: string, serverIp: string) {
  // 入口同步主题：用户点击后再读系统配色与 localStorage，比脚本首载时更可靠
  attachSystemThemeListener();
  hydrateThemeAtChatOpen();

  // 如果已有实例，先清理
  if (currentRoot && currentContainer) {
    currentRoot.unmount();
    currentContainer.remove();
  }
  
  // 创建容器
  const container = document.createElement('div');
  container.id = 'sse-ai-chat-root';
  container.setAttribute('data-sse-react-chat', '1');
  container.setAttribute('data-sse-enlighter-skip', '1');
  container.setAttribute(
    'data-sse-theme',
    useThemeStore.getState().resolvedTheme
  );
  document.body.appendChild(container);
  
  // 锁定 body 滚动
  document.body.style.overflow = 'hidden';
  
  // 创建 React 根节点
  const root = createRoot(container);
  currentRoot = root;
  currentContainer = container;
  
  // 卸载函数
  const onClose = () => {
    root.unmount();
    container.remove();
    document.body.style.overflow = '';
    currentRoot = null;
    currentContainer = null;
  };
  
  // 渲染应用
  root.render(
    <App serverId={serverId} serverName={serverName} serverIp={serverIp} onClose={onClose} />
  );
}

// 检查登录状态，未登录则跳转登录页
function redirectToLogin() {
  const config = (window as any).sseAiOpenerConfig || {};
  const isLoggedIn = config.is_logged_in !== undefined
    ? config.is_logged_in
    : typeof (window as any).sseConfig !== 'undefined' && !!(window as any).sseConfig?.nonce;

  if (isLoggedIn) return true;

  const _win = (window as any)._win || {};
  if (_win.sign_type === 'page') {
    window.location.href = _win.signin_url || '/wp-login.php';
  } else {
    const trigger = document.createElement('a');
    trigger.className = 'signin-loader';
    trigger.href = 'javascript:;';
    document.body.appendChild(trigger);
    trigger.click();
    trigger.remove();
  }
  return false;
}

// 绑定按钮点击事件
function handleButtonClick(e: MouseEvent) {
  const target = e.target as HTMLElement;
  const button = target.closest('.sse-ai-chat') as HTMLElement;

  if (button) {
    e.preventDefault();
    e.stopPropagation();

    if (!redirectToLogin()) return;

    const serverId = parseInt(button.getAttribute('data-server-id') || '0', 10);
    const serverName = button.getAttribute('data-server-name') || '';
    const serverIp = button.getAttribute('data-server-ip') || '';

    if (serverId) {
      openAiChat(serverId, serverName, serverIp);
    }
  }
}

// 立即执行初始化
(function init() {
  if (typeof window === 'undefined') return;
  
  // 设置全局函数
  (window as any).openAiChat = openAiChat;
  
  // 绑定按钮点击事件
  document.addEventListener('click', handleButtonClick);
})();

// 防止 tree-shaking
(window as any).__sse_ai_chat_loaded = true;